import type { Workspace } from '@sunstone/api-client/gen/deployments/v1/deployments_pb';
import { ACTION_BLOCKING_STATES } from './WorkspaceVersionStatus';

export interface ReleaseActionGatingOptions {
  /** Disables while a mutation this specific call site owns is in flight
   * (e.g. previewDiff.isPending for "open the dialog"). */
  isPending?: boolean;
  /** True when the workspace has no current_release_id (legacy/Duplo-
   * discovered tenant with no apps.tfvars.json backfilled yet). */
  noCurrentRelease?: boolean;
}

export interface ReleaseActionGatingResult {
  disabled: boolean;
}

/**
 * Single source of truth for "is opening the promote/upgrade dialog allowed
 * on this workspace right now," shared by WorkspacePage's "Promote
 * Workspace" trigger and WorkspaceDetail's "Preview Upgrade to X" trigger -
 * both hide entirely (not disable) whenever this reports `disabled: true`.
 * Each call site still ANDs its own call-specific extra condition on top of
 * `disabled` (WorkspacePage: workspace.isDeprecated; WorkspaceDetail:
 * !nextRelease and workspace.status === PROVISIONING) - those aren't
 * duplicated logic worth forcing into one shape, since they mean genuinely
 * different things at each site (re-applying a deprecated *current* release
 * vs. an upgrade target that's a different, presumably-active release).
 */
export function getReleaseActionGating(
  workspace: Workspace | undefined,
  opts: ReleaseActionGatingOptions = {},
): ReleaseActionGatingResult {
  if (!workspace || opts.isPending) return { disabled: true };
  if (opts.noCurrentRelease) return { disabled: true };
  if (ACTION_BLOCKING_STATES.has(workspace.upgradeStatus)) {
    return { disabled: true };
  }
  return { disabled: false };
}
